import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Shield, 
  Zap, 
  DollarSign, 
  CheckCircle2, 
  Star,
  ArrowRight,
  ChevronRight,
  Lock,
  Clock,
  TrendingUp
} from 'lucide-react';
import { AuthModal } from '@/components/auth/AuthModal';
import { FloatingContactButton } from '@/components/shared/FloatingContactButton';

export default function LandingPage() {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [pricingPlans, setPricingPlans] = useState([]);

  // Load pricing plans from backend API
  React.useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        const { pricingAPI } = await import('@/services/api');
        const plans = await pricingAPI.getPlans('tax', true); // Get active tax plans
        setPricingPlans(plans);
      } catch (error) {
        console.error('Error fetching pricing plans:', error);
        // Fallback to default plans on error
        setPricingPlans(defaultPricingPlans);
      }
    };

    fetchPricingPlans();
  }, []);

  const handleGetStarted = () => {
    navigate('/login');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Complete your tax filing in as little as 15 minutes with our streamlined process.',
      color: 'text-primary'
    },
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'Your data is encrypted and protected with enterprise-grade security measures.',
      color: 'text-secondary'
    },
    {
      icon: DollarSign,
      title: 'Maximum Refund',
      description: 'Our smart system finds every deduction to maximize your tax refund.',
      color: 'text-success'
    },
    {
      icon: Clock,
      title: 'Save & Resume',
      description: 'Work at your own pace. Save your progress and return anytime.',
      color: 'text-accent'
    }
  ];

  // Default pricing plans (fallback)
  const defaultPricingPlans = [
    {
      name: 'Basic',
      price: 99,
      description: 'For simple tax situations',
      features: [
        'W-2 income only',
        'Standard deductions',
        'Federal + State filing',
        'Email support'
      ],
      popular: false,
      active: true
    },
    {
      name: 'Standard',
      price: 199,
      description: 'For homeowners and investors',
      features: [
        'Everything in Basic',
        'Multiple income sources',
        'Itemized deductions',
        'Priority support',
        'Audit assistance'
      ],
      popular: true,
      active: true
    },
    {
      name: 'Premium',
      price: 349,
      description: 'For complex tax situations',
      features: [
        'Everything in Standard',
        'Business income/expenses',
        'Rental property income',
        'Tax expert review',
        'Full audit protection'
      ],
      popular: false,
      active: true
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5">
      {/* Navbar */}
      <nav className="navbar-glass sticky top-0 z-50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary shadow-lg">
                <FileText className="h-6 w-6 text-primary-foreground" />
              </div>
              <span className="text-2xl font-bold text-foreground">Kosha Tax</span>
            </div>
            <div className="hidden items-center gap-6 md:flex">
              <a href="#features" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                Pricing
              </a>
              <a href="#how-it-works" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                How It Works
              </a>
              <Button variant="ghost" onClick={handleSignIn}>
                Sign In
              </Button>
              <Button className="btn-glossy" onClick={handleGetStarted}>
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden px-6 py-20 md:py-32">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="space-y-8">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                <Star className="mr-1 h-3 w-3" />
                Trusted by Taxpayers Nationwide
              </Badge>
              <h1 className="text-5xl font-bold leading-tight text-foreground md:text-6xl lg:text-7xl">
                File Your Taxes{' '}
                <span className="gradient-text">in Minutes</span>
              </h1>
              <p className="text-xl text-muted-foreground md:text-2xl">
                Maximum refund guaranteed. Simple, secure, and surprisingly affordable.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Button 
                  size="lg" 
                  className="btn-glossy text-lg h-14 px-8"
                  onClick={handleGetStarted}
                >
                  Start Free Filing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="h-14 px-8 text-lg glass"
                  onClick={handleSignIn}
                >
                  Sign In
                </Button>
              </div>
              <div className="flex items-center gap-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <span>No credit card required</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-success" />
                  <span>100% accurate</span>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="card-glossy rounded-2xl p-8 shadow-2xl">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">Your Refund</span>
                    <Badge className="bg-success text-success-foreground">Estimated</Badge>
                  </div>
                  <div className="text-5xl font-bold text-success">$3,254</div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                      <span className="text-sm">Federal</span>
                      <span className="font-semibold">$2,450</span>
                    </div>
                    <div className="flex items-center justify-between rounded-lg bg-muted/50 p-3">
                      <span className="text-sm">State</span>
                      <span className="font-semibold">$804</span>
                    </div>
                  </div>
                  <Button className="w-full btn-glossy">
                    Start Your Filing
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="text-4xl font-bold text-foreground md:text-5xl">Why Choose Kosha?</h2>
            <p className="mt-4 text-xl text-muted-foreground">
              Everything you need for a stress-free tax season
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="card-elevated group hover:scale-105 transition-all">
                  <CardContent className="p-6">
                    <div className="mb-4 inline-flex rounded-xl bg-primary/10 p-3">
                      <Icon className={`h-6 w-6 ${feature.color}`} />
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-foreground">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="text-4xl font-bold text-foreground md:text-5xl">Simple 3-Step Process</h2>
            <p className="mt-4 text-xl text-muted-foreground">
              Get your taxes done in no time
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {[
              { step: '1', title: 'Create Account', description: 'Sign up in 30 seconds with your email or Google account' },
              { step: '2', title: 'Answer Questions', description: 'We guide you through simple questions about your income and deductions' },
              { step: '3', title: 'File & Get Refund', description: 'Review, file electronically, and get your refund in as little as 7 days' }
            ].map((item, index) => (
              <div key={index} className="relative">
                <Card className="card-glossy p-8 text-center">
                  <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground shadow-lg">
                    {item.step}
                  </div>
                  <h3 className="mb-3 text-2xl font-semibold text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </Card>
                {index < 2 && (
                  <ChevronRight className="absolute right-0 top-1/2 hidden -translate-y-1/2 translate-x-full text-muted-foreground lg:block" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="px-6 py-20">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="text-4xl font-bold text-foreground md:text-5xl">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-xl text-muted-foreground">
              Choose the plan that fits your tax situation
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={index} 
                className={`card-elevated relative ${plan.popular ? 'ring-2 ring-primary scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground shadow-lg">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-foreground">{plan.name}</h3>
                  <div className="my-4">
                    <span className="text-5xl font-bold text-foreground">${plan.price}</span>
                    <span className="text-muted-foreground">/year</span>
                  </div>
                  <p className="mb-6 text-muted-foreground">{plan.description}</p>
                  <Button 
                    className={`w-full ${plan.popular ? 'btn-glossy' : ''}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={handleGetStarted}
                  >
                    Get Started
                  </Button>
                  <ul className="mt-6 space-y-3">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-success" />
                        <span className="text-sm text-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <Card className="card-glossy border-2 border-primary/20">
            <CardContent className="p-12 text-center">
              <h2 className="mb-4 text-4xl font-bold text-foreground md:text-5xl">
                Ready to Get Your Maximum Refund?
              </h2>
              <p className="mb-8 text-xl text-muted-foreground">
                Start your tax filing journey with Kosha today
              </p>
              <Button 
                size="lg" 
                className="btn-glossy text-lg h-14 px-12"
                onClick={handleGetStarted}
              >
                Start Free Filing Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="mt-4 text-sm text-muted-foreground">
                No credit card required • 100% secure • Maximum refund guaranteed
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-card/50 px-6 py-12">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-8 md:grid-cols-4">
            <div>
              <div className="mb-4 flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <FileText className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="text-lg font-bold">Kosha Tax</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Making tax filing simple and stress-free for everyone.
              </p>
            </div>
            <div>
              <h4 className="mb-4 font-semibold">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-foreground transition-colors cursor-pointer">Features</a></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors cursor-pointer">Pricing</a></li>
                <li><button onClick={handleGetStarted} className="hover:text-foreground transition-colors text-left">Get Started</button></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-semibold">Account</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={handleSignIn} className="hover:text-foreground transition-colors text-left">Sign In</button></li>
                <li><button onClick={handleGetStarted} className="hover:text-foreground transition-colors text-left">Create Account</button></li>
                <li><a href="#pricing" className="hover:text-foreground transition-colors cursor-pointer">View Plans</a></li>
              </ul>
            </div>
            <div>
              <h4 className="mb-4 font-semibold">Resources</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><a href="https://www.irs.gov" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors text-sm">IRS Website</a></li>
                <li><a href="https://www.irs.gov/forms-instructions" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors text-sm">Tax Forms</a></li>
                <li><a href="https://www.irs.gov/help/tax-law-questions" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors text-sm">Tax Help</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 border-t pt-8 text-center text-sm text-muted-foreground">
            <p>© 2026 Kosha Tax. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <AuthModal 
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onModeChange={setAuthMode}
      />

      {/* Floating Contact Button */}
      <FloatingContactButton />
    </div>
  );
}
